import { useEffect, useState, useCallback, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Alphabet, At, Calendar3, InfoCircleFill, Telephone } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { cents, showMoney } from "./Data/Money";
import { showBrowserDate } from "./Data/Date";
import { createApiService } from "./shared/apiConfig";
import {
    GroupParkerApi,
    ServerRequestsGroupParkerPreviewResponse,
    ServerRequestsSubscriptionGroupContractRuleDto,
} from "./shared/api/axios-client";
import { LocalDate } from "@js-joda/core";

const groupParkerApi = createApiService(GroupParkerApi);

type AssignGroupOption = {
    id: number;
    name: string;
    contractRules?: ServerRequestsSubscriptionGroupContractRuleDto[] | null;
};

export interface PendingGroupParker {
    key: string;
    name: string;
    emailAddress: string;
    phoneNumber: string;
    startDate: string; // yyyy-MM-dd
    previewPrice: number;
    previewSubtotal?: number;
    previewParkerFees?: number;
    isCurrentPeriod: boolean;
    billingStartsOn?: string | null;
    isExtraSlot: boolean;
    subscriptionGroupId?: number | null;
    subscriptionGroupName?: string | null;
}

interface FormValues {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    startDate: string;
    subscriptionGroupId: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    subscriptionId: number;
    subscriptionStart: LocalDate;
    pendingAssignCount: number;
    /** Cart rows not yet checked out — used for same-group contract tier preview. */
    pendingParkers?: Pick<PendingGroupParker, "subscriptionGroupId">[];
    fillsExtraSlot: boolean;
    canAssignAnother: boolean;
    isAccountsReceivable?: boolean;
    /** Enhanced groups: optional group picker. */
    isEnhancedGroup?: boolean;
    groups?: AssignGroupOption[];
    defaultSubscriptionGroupId?: number | null;
    /** Plan monthly unit price in cents — for contract rule full-price display. */
    basePriceCents?: number;
    onAddToCart: (pending: PendingGroupParker, options?: { assignAnother?: boolean }) => void;
}

/** Assign form → add to cart only (no persist / no pay). */
export function GroupParkerAddModal({
    show,
    onHide,
    subscriptionId,
    subscriptionStart,
    pendingAssignCount,
    pendingParkers = [],
    fillsExtraSlot,
    canAssignAnother,
    isAccountsReceivable = false,
    isEnhancedGroup = false,
    groups = [],
    defaultSubscriptionGroupId,
    basePriceCents = 0,
    onAddToCart,
}: Props) {
    const today = showBrowserDate(LocalDate.now());
    const subStartStr = showBrowserDate(subscriptionStart);
    const minDate = subStartStr > today ? subStartStr : today;

    const { register, handleSubmit, watch, reset, setFocus, formState: { errors, isSubmitting } } = useForm<FormValues>({
        defaultValues: {
            name: "",
            emailAddress: "",
            phoneNumber: "",
            startDate: minDate,
            subscriptionGroupId: defaultSubscriptionGroupId ? String(defaultSubscriptionGroupId) : "",
        },
    });
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    const nameRegister = register("name", { required: "Name is required" });

    const focusName = useCallback(() => {
        requestAnimationFrame(() => {
            setFocus("name");
            nameInputRef.current?.focus();
            nameInputRef.current?.select();
        });
    }, [setFocus]);

    const [preview, setPreview] = useState<ServerRequestsGroupParkerPreviewResponse | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const watchedStart = watch("startDate");
    const watchedGroupId = watch("subscriptionGroupId");
    const selectedGroupId = watchedGroupId ? Number(watchedGroupId) : null;
    const selectedGroup = selectedGroupId
        ? groups.find(g => g.id === selectedGroupId)
        : undefined;
    const selectedContractRules = (selectedGroup?.contractRules ?? []).filter(r => r != null);
    const pendingSameGroupAssignCount = selectedGroupId
        ? pendingParkers.filter(p => p.subscriptionGroupId === selectedGroupId).length
        : 0;

    useEffect(() => {
        if (!show || !watchedStart) return;
        let cancelled = false;
        setPreviewLoading(true);
        groupParkerApi
            .groupParkerPreview({
                subscriptionId,
                serverRequestsGroupParkerPreviewRequest: {
                    startDate: watchedStart as any,
                    pendingAssignCount,
                    pendingSameGroupAssignCount,
                    subscriptionGroupId: selectedGroupId,
                },
            })
            .then(r => { if (!cancelled) setPreview(r.data); })
            .catch(() => { if (!cancelled) setPreview(null); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [watchedStart, watchedGroupId, show, subscriptionId, pendingAssignCount, pendingSameGroupAssignCount, selectedGroupId]);

    useEffect(() => {
        if (show) {
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                startDate: minDate,
                subscriptionGroupId: defaultSubscriptionGroupId ? String(defaultSubscriptionGroupId) : "",
            });
            setPreview(null);
        }
    }, [show, minDate, reset, defaultSubscriptionGroupId]);

    useEffect(() => {
        if (!show) return;
        const timer = window.setTimeout(focusName, 50);
        return () => window.clearTimeout(timer);
    }, [show, focusName]);

    const handleClose = () => {
        reset();
        setPreview(null);
        onHide();
    };

    const addToCart = (data: FormValues, assignAnother: boolean) => {
        if (!preview) return;
        const billingOn = preview.billingStartsOn as unknown as string | null | undefined;
        const groupId = data.subscriptionGroupId ? Number(data.subscriptionGroupId) : null;
        const groupName = groupId
            ? groups.find(g => g.id === groupId)?.name ?? null
            : null;
        onAddToCart({
            key: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: data.name.trim(),
            emailAddress: data.emailAddress.trim(),
            phoneNumber: data.phoneNumber.trim(),
            startDate: data.startDate,
            previewPrice: preview.subtotal ?? 0,
            previewSubtotal: preview.subtotal ?? 0,
            previewParkerFees: 0,
            isCurrentPeriod: preview.isCurrentPeriod ?? false,
            billingStartsOn: billingOn ?? null,
            isExtraSlot: fillsExtraSlot,
            subscriptionGroupId: groupId,
            subscriptionGroupName: groupName,
        }, { assignAnother });

        if (assignAnother) {
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                startDate: data.startDate,
                subscriptionGroupId: data.subscriptionGroupId,
            });
            setPreview(null);
            focusName();
            return;
        }
        handleClose();
    };

    const submitDisabled = !preview || previewLoading || isSubmitting;

    return (
        <Modal show={show} onHide={handleClose} centered fullscreen="sm-down" onEntered={focusName}>
            <Modal.Header closeButton className="border-0 pb-0 px-3 pt-3">
                <HeaderText>Assign Parker</HeaderText>
            </Modal.Header>
            <Modal.Body className="pt-2 px-3">
                <Form onSubmit={e => e.preventDefault()}>
                    <Input
                        type="text"
                        icon={<Alphabet />}
                        label="Name"
                        placeholder="Full name"
                        {...nameRegister}
                        ref={el => {
                            nameRegister.ref(el);
                            nameInputRef.current = el as HTMLInputElement | null;
                        }}
                        error={errors.name}
                    />
                    <Input
                        type="email"
                        icon={<At />}
                        label="Email address (optional)"
                        placeholder="parker@example.com"
                        {...register("emailAddress", {
                            validate: value => {
                                const trimmed = value.trim();
                                if (!trimmed) return true;
                                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) || "Enter a valid email";
                            },
                        })}
                        error={errors.emailAddress}
                    />
                    <PhoneInput
                        icon={<Telephone />}
                        label="Phone Number (Optional)"
                        placeholder="555-555-5555"
                        value={watch("phoneNumber") ?? ""}
                        {...register("phoneNumber", {
                            pattern: {
                                value: /^$|^\d{3}-\d{3}-\d{4}$/,
                                message: "Phone number must be in format ###-###-####",
                            },
                        })}
                        error={errors.phoneNumber}
                    />
                    <Input
                        type="date"
                        icon={<Calendar3 />}
                        label="Start date"
                        min={minDate}
                        {...register("startDate", { required: "Start date is required" })}
                        error={errors.startDate}
                    />
                    {isEnhancedGroup && groups.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label className="small text-muted">Group (optional)</Form.Label>
                            <Form.Select {...register("subscriptionGroupId")}>
                                <option value="">Unassigned</option>
                                {groups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    <PreviewSummary
                        preview={preview}
                        loading={previewLoading}
                        groupName={selectedGroup?.name}
                        contractRules={selectedContractRules}
                        isAccountsReceivable={!!isAccountsReceivable}
                        basePriceCents={basePriceCents}
                    />
                    <div className="d-grid d-sm-flex gap-2 mt-3 justify-content-sm-end">
                        {canAssignAnother && (
                                <Button
                                    type="button"
                                    className="px-4"
                                    disabled={submitDisabled}
                                    onClick={handleSubmit(data => addToCart(data, true))}
                                >
                                    Confirm And Add Another
                                </Button>
                            )}
                        <Button
                            type="button"
                            className="btn-main px-4"
                            disabled={submitDisabled}
                            loading={isSubmitting}
                            onClick={handleSubmit(data => addToCart(data, false))}
                        >
                            Confirm
                        </Button>
                        
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

function formatContractRuleLine(
    r: ServerRequestsSubscriptionGroupContractRuleDto,
    basePriceCents: number,
): string {
    const range = `${r.min ?? 0}–${r.max ?? "∞"}`;
    if (!r.price || r.price === 0) return `${range}: Free`;
    const amount = Math.abs(r.price);
    // type: 0 discount, 1 fee (matches ContractDetailsModal)
    const fullCents = r.type === 1
        ? basePriceCents + amount
        : Math.max(basePriceCents - amount, 0);
    if (fullCents === 0) return `${range}: Free`;
    return `${range}: ${showMoney(cents(fullCents))}/mo`;
}

function PreviewSummary({
    preview,
    loading,
    groupName,
    contractRules = [],
    isAccountsReceivable = false,
    basePriceCents = 0,
}: {
    preview: ServerRequestsGroupParkerPreviewResponse | null;
    loading: boolean;
    groupName?: string;
    contractRules?: ServerRequestsSubscriptionGroupContractRuleDto[];
    isAccountsReceivable?: boolean;
    basePriceCents?: number;
}) {
    if (loading) {
        return (
            <div
                className="alert alert-muted-gold mb-0 small placeholder-glow"
                role="status"
                aria-label="Calculating cost"
            >
                <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="d-flex align-items-start gap-2 flex-grow-1 min-w-0">
                        <span
                            className="placeholder rounded-circle flex-shrink-0"
                            style={{ width: 16, height: 16, marginTop: 2 }}
                        />
                        <div className="flex-grow-1 min-w-0">
                            <span className="placeholder col-5 rounded d-block" style={{ height: 14 }} />
                            <span className="placeholder col-10 rounded d-block mt-2" style={{ height: 11 }} />
                            <span className="placeholder col-7 rounded d-block mt-1" style={{ height: 11 }} />
                        </div>
                    </div>
                    <span className="placeholder rounded flex-shrink-0" style={{ width: 52, height: 14 }} />
                </div>
            </div>
        );
    }
    if (!preview) return null;

    const hasContract = contractRules.length > 0;
    const sortedRules = [...contractRules].sort(
        (a, b) => (a.min ?? 0) - (b.min ?? 0),
    );

    if (!preview.isCurrentPeriod && (preview.subtotal ?? 0) === 0) {
        return (
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                <InfoCircleFill className="flex-shrink-0 mt-1" />
                <span>
                    {isAccountsReceivable ? (
                        <>
                            Starts after the current billing period. Will be included on an amended or future invoice.
                        </>
                    ) : (
                        <>
                            Starts after the current billing period. No charge at checkout.
                        </>
                    )}
                </span>
            </div>
        );
    }

    if ((preview.subtotal ?? 0) === 0) {
        return (
            <div className="alert alert-muted-gold d-flex flex-column gap-2 mb-0 small" role="alert">
                <div className="d-flex align-items-start gap-2">
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>Assigning to an open spot. No charge at checkout.</span>
                </div>
                {hasContract && groupName && (
                    <div className="ps-4">
                        <div className="fw-semibold mb-1">{groupName} contract</div>
                        {sortedRules.map((r, i) => (
                            <div key={i} className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.35 }}>
                                {formatContractRuleLine(r, basePriceCents)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="alert alert-muted-gold d-flex align-items-start justify-content-between gap-2 mb-0 small" role="alert">
            <div className="d-flex align-items-start gap-2 min-w-0">
                <InfoCircleFill className="flex-shrink-0 mt-1" />
                <div className="min-w-0">
                    <div>Estimated price</div>
                    {hasContract && groupName ? (
                        <div className="mt-1">
                            <div className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.3 }}>
                                Includes <strong>{groupName}</strong> contract pricing (prorated from start date).
                            </div>
                            {sortedRules.map((r, i) => (
                                <div key={i} className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.35 }}>
                                    {formatContractRuleLine(r, basePriceCents)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.3, marginTop: 2 }}>
                            Prices are based on start date and the prorating rules for the facility&apos;s parking plans.
                        </div>
                    )}
                </div>
            </div>
            <span className="fw-semibold flex-shrink-0">{showMoney(cents(preview.subtotal ?? 0))}</span>
        </div>
    );
}
